import React, {useState} from 'react';
import {useForm, Controller, SubmitHandler} from 'react-hook-form';
import {Box, Typography, IconButton, InputAdornment, Alert, CircularProgress} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import TextField from 'components/inputs/TextField';
import Button from 'components/buttons/Button';

export interface LoginFormInputs {
   username: string;
   password: string;
   remember: boolean;
}

interface LoginFormProps {
   onSubmit?: (data: LoginFormInputs) => Promise<void> | void;
}

// TypeScript version of login form using react-hook-form + MUI
export default function LoginForm({onSubmit: externalSubmit}: LoginFormProps) {
   const {
      control,
      handleSubmit,
      formState: {errors, isSubmitting},
      setError,
   } = useForm<LoginFormInputs>({
      defaultValues: {username: '', password: '', remember: false},
      mode: 'onTouched',
   });

   const [showPassword, setShowPassword] = useState(false);
   const [serverError, setServerError] = useState('');

   const togglePassword = () => setShowPassword((s) => !s);

   const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
      setServerError('');
      try {
         if (externalSubmit) {
            await externalSubmit(data);
         } else {
            // eslint-disable-next-line no-console
            console.log('Login data', data);
         }
      } catch (err: any) {
         setServerError(err?.message || 'Something went wrong. Please try again.');
      }
   };

   return (
      <Box
         component="form"
         onSubmit={handleSubmit(onSubmit)}
         sx={{width: 360, maxWidth: '100%', mx: 'auto', p: 3}}
         noValidate
      >
         <Typography variant="h5" component="h1" gutterBottom>
            Sign in
         </Typography>

         {serverError && (
            <Alert severity="error" sx={{mb: 2}}>
               {serverError}
            </Alert>
         )}

         {/* <Controller
            name="email"
            control={control}
            rules={{
               required: 'Email is required',
               pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email',
               },
            }}
            render={({field}) => (
               <TextField
                  {...field}
                  label="Email"
                  margin="normal"
                  fullWidth
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : ''}
               />
            )}
         /> */}
         <Controller
            name="username"
            control={control}
            rules={{
               required: 'Username is required',
            }}
            render={({field}) => (
               <TextField
                  {...field}
                  label="Username"
                  margin="normal"
                  fullWidth
                  autoComplete="username"
                  error={!!errors.username}
                  helperText={errors.username ? errors.username.message : ''}
               />
            )}
         />

         <Controller
            name="password"
            control={control}
            rules={{
               required: 'Password is required',
               // minLength: {value: 6, message: 'Password must be at least 6 characters'},
            }}
            render={({field}) => (
               <TextField
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  margin="normal"
                  fullWidth
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password ? errors.password.message : ''}
                  InputProps={{
                     endAdornment: (
                        <InputAdornment position="end">
                           <IconButton
                              onClick={togglePassword}
                              edge="end"
                              aria-label="toggle password visibility"
                           >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                           </IconButton>
                        </InputAdornment>
                     ),
                  }}
               />
            )}
         />

         {/* <Controller
            name="remember"
            control={control}
            render={({field}) => (
               <FormControlLabel
                  control={<Checkbox {...field} checked={!!field.value} />}
                  label="Remember me"
               />
            )}
         /> */}

         <Box sx={{position: 'relative', mt: 1}}>
            <Button type="submit" fullWidth disabled={isSubmitting} size="large">
               Sign in
            </Button>
            {isSubmitting && (
               <CircularProgress
                  size={24}
                  sx={{position: 'absolute', top: '50%', left: '50%', mt: '-12px', ml: '-12px'}}
               />
            )}
         </Box>

         {/* <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 2}}>
            <Typography variant="body2" component="span">
               <a href="#" onClick={(e) => e.preventDefault()}>
                  Forgot password?
               </a>
            </Typography>

            <Typography variant="body2" component="span">
               <a href="#" onClick={(e) => e.preventDefault()}>
                  Sign up
               </a>
            </Typography>
         </Box> */}
      </Box>
   );
}
